import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";

/**
 * useDegradedMode - Hook to detect and manage degraded app state
 * 
 * Degraded mode = we tried to reach core endpoints and something failed.
 * We DON'T block the app — we just show a banner + retry.
 * 
 * Features:
 * - Auto-retry with configurable interval
 * - Clear reason/error messages
 * - Manual retry capability
 */
export function useDegradedMode() {
  const [degraded, setDegraded] = useState(false);
  const [reason, setReason] = useState("");
  const [lastError, setLastError] = useState(null);
  const [nextRetryAt, setNextRetryAt] = useState(null);

  const inFlightRef = useRef(false);
  const retryTimerRef = useRef(null);

  const scheduleRetry = (ms = 10_000) => {
    const when = Date.now() + ms;
    setNextRetryAt(when);
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = setTimeout(() => {
      ping();
    }, ms);
  };

  const ping = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      // Check if we have a token first
      const token = localStorage.getItem('token');
      if (!token) {
        // No token = not logged in, not degraded (just unauthenticated)
        setDegraded(false);
        setReason("");
        setLastError(null);
        setNextRetryAt(null);
        return;
      }
      
      // Keep this cheap + representative.
      // /api/auth/me is a good health check for authenticated users
      await api.get("/auth/me");
      setDegraded(false);
      setReason("");
      setLastError(null);
      setNextRetryAt(null);
      console.log('[DegradedMode] Health check passed ✅');
    } catch (e) {
      setDegraded(true);

      const status = e?.response?.status;
      if (status === 401) {
        setReason("Session expired");
      } else if (status === 403) {
        setReason("Access denied");
      } else if (status >= 500) {
        setReason("Server trouble");
      } else if (!navigator.onLine) {
        setReason("No network connection");
      } else {
        setReason("Network / startup issue");
      }

      setLastError(e?.message || e?.toString?.() || "Unknown error");
      console.warn('[DegradedMode] Health check failed:', reason, e?.message);
      scheduleRetry(10_000);
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    // Initial ping after a short delay to let auth hydrate
    const initialDelay = setTimeout(() => {
      ping();
    }, 1000);
    
    return () => {
      clearTimeout(initialDelay);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [ping]);

  return { degraded, reason, lastError, nextRetryAt, retry: ping };
}
